/**
 * City services: an ambulance comes for a body, a tow truck for a wreck — when a player is
 * near enough to see it. The vehicle spawns on a road 50–80 tiles away, drives to the spot
 * with the pursuit driver (no red lights), works there for a few seconds (the body / wreck
 * disappears), then joins the traffic and despawns like any other car.
 */
import { RF_H, RF_SIDEWALK, RF_V } from '../world/generator';
import { driveNpc, laneCentre } from './ai';
import { makeVehicle, type VehicleEntity } from './entities';
import type { Game } from './game';
import { vehicleBlocked } from './physics';
import { WorldStream } from '../engine/world-stream';
import { doorstep } from './property';
import { BIN_NPC_AFTER, collectBin } from './garbage';

export interface ServiceCall { kind: 'ambulance' | 'tow' | 'fire' | 'garbage'; x: number; y: number; t: number; vid?: number; stage: 'drive' | 'work' | 'done'; work: number; /** person / vehicle id it came for */ target: number; /** fire: the building key */ key?: string }

const MAX_CALLS = 3;
const CALL_DELAY = 6;      // seconds a body / wreck lies there before the call goes out
const DRIVE_LIMIT = 75;    // give up driving after this long
const WORK_TIME = 7;
const FIRE_WORK = 18;      // seconds a fire engine takes to make a damaged building whole again

export function stepServices(game: Game, dt: number): void {
  const calls = game.calls;
  // calls a player is working as a job (jobs.ts `svc`) are theirs: no NPC rig comes
  const mine = new Set<string>();
  for (const s of game.players.values()) { const sv = s.job?.svc; if (!sv) continue; if (sv.target !== undefined) mine.add(sv.kind === 'tow' ? `v${sv.target}` : `p${sv.target}`); if (sv.key) mine.add(`b${sv.key}`); }
  // new calls: a body (NPC) or a wreck (nobody's) with a player within 120 tiles
  if (calls.length < MAX_CALLS) {
    for (const q of game.people) {
      if (!q.dead || q.isPlayer || q.animal || q.deadFor < CALL_DELAY || q.vehicle || q.inside) continue; // (no ambulance for a cat)
      if (calls.some((c) => c.target === q.id) || mine.has(`p${q.id}`)) continue;
      if (game.nearestPlayerDist(q.x, q.y) > 120 || !game.world.roadAt(q.x, q.y) && !nearRoad(game, q.x, q.y)) continue;
      if (calls.length >= MAX_CALLS) break;
      calls.push({ kind: 'ambulance', x: q.x, y: q.y, t: 0, stage: 'drive', work: 0, target: q.id });
    }
    for (const v of game.vehicles) {
      if (!v.wrecked || v.owner !== undefined || v.wet > 0 || v.alt > 0.5 || v.driver || v.towedBy !== undefined) continue; // (a wreck on a player's hook is theirs)
      if (calls.some((c) => c.target === -v.id) || mine.has(`v${v.id}`)) continue;
      if ((v.wreckedFor ?? 0) < CALL_DELAY * 2 || game.nearestPlayerDist(v.x, v.y) > 120 || !game.world.roadAt(v.x, v.y)) continue;
      if (calls.length >= MAX_CALLS) break;
      calls.push({ kind: 'tow', x: v.x, y: v.y, t: 0, stage: 'drive', work: 0, target: -v.id });
    }
  }
  // (owner, Sep 16) bins out for hours with nobody on the round: the city's own truck takes them, one call a bin — unless a
  // player on the garbage job is within reach of it (their round, their pay)
  if (calls.length < MAX_CALLS) for (const k of game.pickups) {
    if (k.bin === undefined || k.t < BIN_NPC_AFTER) continue;
    if (calls.some((c) => c.kind === 'garbage' && c.key === k.bin)) continue;
    if (game.nearestPlayerDist(k.x, k.y) > 120) continue;
    if ([...game.players.values()].some((s) => s.job?.svc?.kind === 'garbage' && Math.hypot(s.person.x - k.x, s.person.y - k.y) < 200)) continue;
    if (calls.length >= MAX_CALLS) break;
    calls.push({ kind: 'garbage', x: k.x, y: k.y, t: 0, stage: 'drive', work: 0, target: k.id, key: k.bin });
  }
  // damaged buildings (30 %+, not rubble) near a player: a fire engine repairs them
  if (calls.length < MAX_CALLS) for (const lc of game.world.liveChunks()) {
    for (const b of lc.chunk.buildings) {
      if (!b.damage || b.ruined || b.height <= 0 || b.damage < 0.3 * WorldStream.buildingHp(b)) continue;
      const key = `${b.x},${b.y}`;
      if (calls.some((c) => c.kind === 'fire' && c.key === key) || mine.has(`b${key}`)) continue;
      if (game.nearestPlayerDist(b.x, b.y) > 120 || game.props.get(key)) continue; // (an owned house is the owner's problem)
      if (calls.length >= MAX_CALLS) break;
      const d = doorstep(b, game.seed);
      calls.push({ kind: 'fire', x: d.x, y: d.y, t: 0, stage: 'drive', work: 0, target: 0, key });
    }
    if (calls.length >= MAX_CALLS) break;
  }
  for (let i = calls.length - 1; i >= 0; i--) {
    const c = calls[i];
    c.t += dt;
    if (c.stage === 'done') { calls.splice(i, 1); continue; }
    let v = c.vid !== undefined ? game.vehicles.find((x) => x.id === c.vid) : undefined;
    if (!v) {
      if (c.vid !== undefined || c.t > 2) { // despawned on the way, or the first spawn attempt
        v = spawnService(game, c);
        if (!v) { if (c.t > 30) calls.splice(i, 1); continue; }
        c.vid = v.id;
      } else continue;
    }
    if (v.wrecked || !v.ai) { calls.splice(i, 1); continue; }
    const d = Math.hypot(v.x - c.x, v.y - c.y);
    if (c.stage === 'drive') {
      v.ai.ignorePeds = 5;
      if (!driveNpc(game.world, v, game.vehicles, game.people, game.npcRng, dt, c)) { v.ai = undefined; v.home = 'lost'; calls.splice(i, 1); continue; }
      if (d < 9 || c.t > DRIVE_LIMIT) { c.stage = 'work'; c.work = 0; v.speed = 0; }
    } else {
      // parked at the scene: the body is taken away / the wreck hooked up
      c.work += dt; v.speed = 0;
      if (c.kind === 'fire') {
        // hoses on: the damage drains away; smoke keeps coming from what is left
        const [bx, by] = c.key!.split(',').map(Number); const b = game.world.buildingAt(bx + 0.5, by + 0.5);
        if (!b || b.ruined) { v.ai.chase = false; v.siren = false; v.home = 'lost'; c.stage = 'done'; continue; }
        const hp = WorldStream.buildingHp(b);
        b.damage = Math.max(0, (b.damage ?? 0) - (hp / FIRE_WORK) * dt);
        if (b.damage <= 0.01) { b.damage = 0; v.ai.chase = false; v.siren = false; v.home = 'lost'; v.ai.ignorePeds = 0; c.stage = 'done'; }
        continue;
      }
      if (c.kind === 'garbage') {
        const k = game.pickups.find((q) => q.id === c.target);
        if (!k) { v.ai.chase = false; v.home = 'lost'; v.ai.ignorePeds = 0; c.stage = 'done'; continue; } // (somebody took it first)
        if (c.work >= 4) { collectBin(game, k); v.bags = Math.min(v.def.bags ?? 20, (v.bags ?? 0) + 1); v.ai.chase = false; v.home = 'lost'; v.ai.ignorePeds = 0; c.stage = 'done'; }
        continue;
      }
      if (c.work >= WORK_TIME) {
        if (c.kind === 'ambulance') { const k = game.people.findIndex((q) => q.id === c.target); if (k >= 0 && game.people[k].dead) game.people.splice(k, 1); }
        else { const w = game.vehicles.find((x) => x.id === -c.target); if (w && !w.driver && w.towedBy === undefined) { v.tow = w.id; w.towedBy = v.id; w.home = 'lost'; w.owner = undefined; } } // (owner, Sep 16: the wreck rides off on the hook instead of vanishing; both go the way of lost traffic)
        v.ai.chase = false; v.siren = false; v.home = 'lost'; v.ai.ignorePeds = 0;
        c.stage = 'done';
      }
    }
  }
}

function nearRoad(game: Game, x: number, y: number): boolean {
  for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) if (game.world.roadAt(x + dx, y + dy)) return true;
  return false;
}

function spawnService(game: Game, c: ServiceCall): VehicleEntity | undefined {
  const defs = game.reg.all('vehicle').filter((v) => (c.kind === 'ambulance' ? v.tags.includes('medical') : c.kind === 'fire' ? v.tags.includes('fire') : c.kind === 'garbage' ? (v.bags ?? 0) > 0 : v.tags.includes('service') && v.id.includes('tow')) && v.class !== 'helicopter' && v.class !== 'boat' && v.class !== 'plane');
  const def = defs[0] ?? (c.kind === 'tow' ? game.reg.all('vehicle').find((v) => v.class === 'truck') : undefined);
  if (!def) return undefined;
  const rng = game.npcRng;
  for (let tries = 0; tries < 30; tries++) {
    const a = rng.float() * Math.PI * 2, r = 50 + rng.float() * 30;
    let x = Math.floor(c.x + Math.cos(a) * r) + 0.5, y = Math.floor(c.y + Math.sin(a) * r) + 0.5;
    if (!game.world.loaded(x, y) || !game.world.roadAt(x, y) || game.world.roadFlags(x, y) & RF_SIDEWALK) continue;
    const fl = game.world.roadFlags(x, y); if (((fl & RF_V) !== 0) === ((fl & RF_H) !== 0)) continue; // a plain stretch, not a crossing (a rig born across a corner sat there stuck)
    const hit = game.world.gen.net.hitsAt(Math.floor(x), Math.floor(y)).find((h) => h.road.class !== 'rail' && h.axis === (fl & RF_V ? 'v' : 'h'));
    if (!hit) continue;
    const axis = hit.axis, dir: 1 | -1 = (axis === 'v' ? c.y - y : c.x - x) >= 0 ? 1 : -1;
    const lane = laneCentre(hit, axis, dir, false, def.size[0]); if (axis === 'v') x = lane; else y = lane; // in its own lane, nose down the road
    const angle = axis === 'v' ? (dir === -1 ? 0 : Math.PI) : dir === 1 ? Math.PI / 2 : -Math.PI / 2;
    const v = makeVehicle(def, x, y, angle, 'service');
    if (vehicleBlocked(game.world, v, x, y, angle, 'drive')) continue;
    if (game.vehicles.some((o) => Math.hypot(o.x - x, o.y - y) < 6)) continue; // not on top of a parked car (a rig born stuck never arrived)
    v.fuel = 1; v.siren = c.kind !== 'tow' && c.kind !== 'garbage'; // (lights and siren on the way to the call — and through the reds, ai.ts)
    v.ai = { axis, dir, wait: 0, lost: 0, blockedFor: 0, overtake: 0, stuck: 0, reverse: 0, ignorePeds: 0, redFor: 0, chase: true };
    game.vehicles.push(v);
    return v;
  }
  return undefined;
}
